import { useState, useEffect, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from "recharts";
import {
  Heart,
  Loader2,
  RefreshCw,
  Zap,
  ThumbsUp,
  ThumbsDown,
  Minus,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Smile,
  Frown,
  Meh,
  MessageSquare,
  Target
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SENTIMENT_COLORS = {
  positive: "#10b981",
  neutral: "#6b7280",
  negative: "#ef4444"
};

// Custom Gauge Component
const SentimentGauge = ({ value, darkMode }) => {
  // Value is -1 to 1, normalize to 0-180 degrees
  const normalizedValue = ((value + 1) / 2) * 180;
  const radius = 80;
  const strokeWidth = 12;
  
  const getColor = (val) => {
    if (val > 0.3) return SENTIMENT_COLORS.positive;
    if (val < -0.3) return SENTIMENT_COLORS.negative;
    return SENTIMENT_COLORS.neutral;
  };
  
  const getLabel = (val) => {
    if (val > 0.5) return "Very Positive";
    if (val > 0.3) return "Positive";
    if (val > -0.3) return "Neutral";
    if (val > -0.5) return "Negative";
    return "Very Negative";
  };

  return (
    <div className="flex flex-col items-center">
      <svg width="200" height="120" viewBox="0 0 200 120">
        {/* Background arc */}
        <path
          d="M 20 100 A 80 80 0 0 1 180 100"
          fill="none"
          stroke={darkMode ? '#30363d' : '#e5e7eb'}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* Value arc */}
        <path
          d={`M 20 100 A 80 80 0 0 1 ${20 + (160 * normalizedValue / 180)} ${100 - Math.sin(normalizedValue * Math.PI / 180) * radius}`}
          fill="none"
          stroke={getColor(value)}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* Center text */}
        <text 
          x="100" 
          y="95" 
          textAnchor="middle" 
          fill={darkMode ? 'white' : '#111827'}
          fontSize="24"
          fontWeight="bold"
        >
          {(value * 100).toFixed(0)}
        </text>
        <text 
          x="100" 
          y="115" 
          textAnchor="middle" 
          fill={darkMode ? '#8b949e' : '#6b7280'}
          fontSize="12"
        >
          {getLabel(value)}
        </text>
      </svg>
      <div className="flex items-center justify-between w-full px-4 mt-2">
        <div className="flex items-center gap-1">
          <Frown className="w-4 h-4 text-red-500" />
          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>-100</span>
        </div>
        <div className="flex items-center gap-1">
          <Meh className="w-4 h-4 text-gray-500" />
          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>0</span>
        </div>
        <div className="flex items-center gap-1">
          <Smile className="w-4 h-4 text-emerald-500" />
          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>+100</span>
        </div>
      </div>
    </div>
  );
};

const CustomPieTooltip = ({ active, payload, darkMode }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <div className={`p-3 rounded-lg shadow-lg border ${
        darkMode ? 'bg-[#161b22] border-[#30363d]' : 'bg-white border-gray-200'
      }`}>
        <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {data.name}
        </p>
        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {data.value} competitors ({data.payload.percentage}%)
        </p>
      </div>
    );
  }
  return null;
};

export default function SentimentAnalysis() {
  const { darkMode, region, brand } = useOutletContext() || { darkMode: true, region: 'UAE', brand: 'blackswan' };
  const [sentiments, setSentiments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/advanced/sentiment/${brand}/${region}`);
      setSentiments(res.data.sentiments || []);
      setSummary(res.data.summary || null);
    } catch (error) {
      console.error("Error fetching sentiment data:", error);
    } finally {
      setLoading(false);
    }
  }, [brand, region]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/advanced/sentiment/analyze/${brand}/${region}`);
      toast.success(`Analyzed sentiment for ${res.data.analyzed || 0} competitors!`);
      fetchData();
    } catch (error) {
      toast.error("Failed to analyze sentiment");
    } finally {
      setAnalyzing(false);
    }
  };

  const pieData = summary ? [
    { name: "Positive", value: summary.positive_count, percentage: summary.positive_percentage, color: SENTIMENT_COLORS.positive },
    { name: "Neutral", value: summary.neutral_count, percentage: summary.neutral_percentage, color: SENTIMENT_COLORS.neutral },
    { name: "Negative", value: summary.negative_count, percentage: summary.negative_percentage, color: SENTIMENT_COLORS.negative }
  ] : [];

  const barData = useMemo(
    () => sentiments
      .map(s => ({
        name: s.competitor_name?.substring(0, 15) || 'Unknown',
        sentiment: Math.round(s.sentiment_score * 100),
        fullName: s.competitor_name
      }))
      .sort((a, b) => b.sentiment - a.sentiment),
    [sentiments]
  );

  const topPositive = useMemo(
    () => sentiments
      .filter(s => s.sentiment_score > 0)
      .sort((a, b) => b.sentiment_score - a.sentiment_score)
      .slice(0, 3),
    [sentiments]
  );

  const topNegative = useMemo(
    () => sentiments
      .filter(s => s.sentiment_score < 0)
      .sort((a, b) => a.sentiment_score - b.sentiment_score)
      .slice(0, 3),
    [sentiments]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="sentiment-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#DC143C]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sentiment-analysis-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Sentiment Analysis
          </h1>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            AI-powered analysis of competitor brand perception and sentiment
          </p>
        </div>
        <Button
          onClick={runAnalysis}
          disabled={analyzing}
          className="bg-[#DC143C] hover:bg-[#b91031]"
          data-testid="analyze-sentiment-btn"
        >
          {analyzing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Zap className="w-4 h-4 mr-2" />
          )}
          AI Analysis
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-emerald-500/10">
                <ThumbsUp className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Positive</p>
                <p className={`text-2xl font-bold text-emerald-500`}>
                  {summary?.positive_count || 0}
                </p>
                <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {summary?.positive_percentage || 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gray-500/10">
                <Minus className="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Neutral</p>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {summary?.neutral_count || 0}
                </p>
                <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {summary?.neutral_percentage || 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-red-500/10">
                <ThumbsDown className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Negative</p>
                <p className={`text-2xl font-bold text-red-500`}>
                  {summary?.negative_count || 0}
                </p>
                <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {summary?.negative_percentage || 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-purple-500/10">
                <MessageSquare className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Analyzed</p>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {summary?.total_analyzed || 0}
                </p>
                <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  competitors
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={darkMode ? 'bg-[#161b22]' : ''}>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="details">Competitor Details</TabsTrigger>
          <TabsTrigger value="comparison">Comparison</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-12 gap-6">
            {/* Gauge Card */}
            <div className="col-span-4">
              <Card className={`h-full ${darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}`}>
                <CardHeader>
                  <CardTitle className={darkMode ? 'text-white' : ''}>
                    Overall Sentiment Score
                  </CardTitle>
                  <CardDescription>
                    Market-wide sentiment indicator
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex items-center justify-center py-4">
                  <SentimentGauge 
                    value={summary?.average_sentiment || 0} 
                    darkMode={darkMode}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Pie Chart Card */}
            <div className="col-span-4">
              <Card className={`h-full ${darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}`}>
                <CardHeader>
                  <CardTitle className={darkMode ? 'text-white' : ''}>
                    Sentiment Distribution
                  </CardTitle>
                  <CardDescription>
                    Breakdown by sentiment type
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomPieTooltip darkMode={darkMode} />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Legend */}
                  <div className="flex items-center justify-center gap-4 mt-4">
                    {pieData.map((entry) => (
                      <div key={entry.name} className="flex items-center gap-1.5">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: entry.color }}
                        />
                        <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {entry.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Top/Bottom Performers */}
            <div className="col-span-4 space-y-4">
              {/* Most Positive */}
              <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
                <CardHeader className="pb-2">
                  <CardTitle className={`text-sm ${darkMode ? 'text-white' : ''}`}>
                    <TrendingUp className="w-4 h-4 inline mr-2 text-emerald-500" />
                    Most Positive Sentiment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {topPositive.map((s, idx) => (
                        <div key={s.competitor_id || s.competitor_name || idx} className="flex items-center justify-between">
                          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {s.competitor_name}
                          </span>
                          <Badge className="bg-emerald-500/10 text-emerald-500 border-0">
                            +{Math.round(s.sentiment_score * 100)}
                          </Badge>
                        </div>
                      ))}
                    {topPositive.length === 0 && (
                      <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        No positive sentiments detected
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Most Negative */}
              <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
                <CardHeader className="pb-2">
                  <CardTitle className={`text-sm ${darkMode ? 'text-white' : ''}`}>
                    <TrendingDown className="w-4 h-4 inline mr-2 text-red-500" />
                    Most Negative Sentiment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {topNegative.map((s, idx) => (
                        <div key={s.competitor_id || s.competitor_name || idx} className="flex items-center justify-between">
                          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {s.competitor_name}
                          </span>
                          <Badge className="bg-red-500/10 text-red-500 border-0">
                            {Math.round(s.sentiment_score * 100)}
                          </Badge>
                        </div>
                      ))}
                    {topNegative.length === 0 && (
                      <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        No negative sentiments detected
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Details Tab */}
        <TabsContent value="details" className="mt-4">
          <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
            <CardHeader>
              <CardTitle className={darkMode ? 'text-white' : ''}>
                Competitor Sentiment Details
              </CardTitle>
              <CardDescription>
                Detailed breakdown of each competitor's sentiment analysis
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {sentiments.map((sentiment, idx) => {
                  const score = sentiment.sentiment_score || 0;
                  const isPositive = score > 0.3;
                  const isNegative = score < -0.3;
                  
                  return (
                    <div 
                      key={sentiment.competitor_id || idx}
                      className={`p-4 rounded-xl ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            isPositive ? 'bg-emerald-500/10' :
                            isNegative ? 'bg-red-500/10' :
                            'bg-gray-500/10'
                          }`}>
                            {isPositive ? (
                              <Smile className="w-5 h-5 text-emerald-500" />
                            ) : isNegative ? (
                              <Frown className="w-5 h-5 text-red-500" />
                            ) : (
                              <Meh className="w-5 h-5 text-gray-500" />
                            )}
                          </div>
                          <div>
                            <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {sentiment.competitor_name}
                            </p>
                            <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {sentiment.post_count || 0} posts analyzed
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${
                            isPositive ? 'text-emerald-500' :
                            isNegative ? 'text-red-500' :
                            darkMode ? 'text-white' : 'text-gray-900'
                          }`}>
                            {score > 0 ? '+' : ''}{Math.round(score * 100)}
                          </p>
                          <Badge className={`mt-1 border-0 ${
                            isPositive ? 'bg-emerald-500/10 text-emerald-500' :
                            isNegative ? 'bg-red-500/10 text-red-500' :
                            'bg-gray-500/10 text-gray-500'
                          }`}>
                            {sentiment.sentiment_label || (isPositive ? 'Positive' : isNegative ? 'Negative' : 'Neutral')}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Additional AI insights if available */}
                      {sentiment.key_themes && (
                        <div className="mt-3 pt-3 border-t border-dashed" style={{ borderColor: darkMode ? '#30363d' : '#e5e7eb' }}>
                          <p className={`text-xs font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Key Themes:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {sentiment.key_themes.map((theme, i) => (
                              <Badge key={`${sentiment.competitor_id || sentiment.competitor_name || ''}-theme-${theme}-${i}`} className="bg-blue-500/10 text-blue-500 border-0 text-xs">
                                {theme}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {sentiment.brand_perception && (
                        <p className={`text-sm mt-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          <Target className="w-3 h-3 inline mr-1" />
                          {sentiment.brand_perception}
                        </p>
                      )}
                      
                      {sentiment.platforms && sentiment.platforms.length > 0 && (
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            Platforms:
                          </span>
                          {sentiment.platforms.map((p, i) => (
                            <Badge key={`${sentiment.competitor_id || sentiment.competitor_name || ''}-platform-${p}-${i}`} variant="outline" className="text-xs">
                              {p}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {sentiments.length === 0 && (
                  <div className={`text-center py-12 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    <Heart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">No sentiment data available</p>
                    <p className="text-sm mt-1">Click "AI Analysis" to analyze competitor sentiment</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Comparison Tab */}
        <TabsContent value="comparison" className="mt-4">
          <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
            <CardHeader>
              <CardTitle className={darkMode ? 'text-white' : ''}>
                Sentiment Comparison Chart
              </CardTitle>
              <CardDescription>
                Compare sentiment scores across all competitors
              </CardDescription>
            </CardHeader>
            <CardContent>
              {barData.length > 0 ? (
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={barData.slice(0, 15)}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                    >
                      <CartesianGrid 
                        strokeDasharray="3 3" 
                        stroke={darkMode ? '#30363d' : '#e5e7eb'}
                        horizontal={true}
                        vertical={false}
                      />
                      <XAxis 
                        type="number"
                        domain={[-100, 100]}
                        tick={{ fill: darkMode ? '#8b949e' : '#6b7280', fontSize: 12 }}
                        axisLine={{ stroke: darkMode ? '#30363d' : '#e5e7eb' }}
                      />
                      <YAxis 
                        type="category"
                        dataKey="name"
                        tick={{ fill: darkMode ? '#8b949e' : '#6b7280', fontSize: 12 }}
                        axisLine={{ stroke: darkMode ? '#30363d' : '#e5e7eb' }}
                        width={100}
                      />
                      <Tooltip
                        formatter={(value, name, props) => [
                          `${value > 0 ? '+' : ''}${value}`,
                          props.payload.fullName
                        ]}
                        contentStyle={{
                          backgroundColor: darkMode ? '#161b22' : '#fff',
                          border: `1px solid ${darkMode ? '#30363d' : '#e5e7eb'}`,
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: darkMode ? '#fff' : '#111827' }}
                      />
                      <Bar 
                        dataKey="sentiment" 
                        radius={[0, 4, 4, 0]}
                      >
                        {barData.slice(0, 15).map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`}
                            fill={
                              entry.sentiment > 30 ? SENTIMENT_COLORS.positive :
                              entry.sentiment < -30 ? SENTIMENT_COLORS.negative :
                              SENTIMENT_COLORS.neutral
                            }
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className={`text-center py-12 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No data to compare</p>
                  <p className="text-sm mt-1">Run sentiment analysis first</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
